import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Briefcase, Users } from 'lucide-react';
interface SlideScaleVsRelevanceProps {
  onContinue: () => void;
}
export function SlideScaleVsRelevance({
  onContinue
}: SlideScaleVsRelevanceProps) {
  const [stage, setStage] = useState(0);
  const agency = ['Too expensive for high campaign volumes', 'Too slow for frequent local adaptation', 'Too fragmented across brands and markets', 'Too dependent on repeated briefing cycles', 'Too difficult to measure across countries'];
  const internal = ['More output without greater relevance', 'Volume without strategic depth', 'Production without continuous optimization', 'Fixed resources regardless of demand', 'Limited specialist creative capability'];
  useEffect(() => {
    const timers = [300, 1000, 1700, 2500].map((ms, idx) => setTimeout(() => setStage(idx + 1), ms));
    return () => timers.forEach(clearTimeout);
  }, []);
  return <div className="h-full w-full flex flex-col px-8 py-10 md:px-20 md:py-12 max-w-7xl mx-auto" data-id="element-1013" data-mp="19k2q75s15rtd">
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} className="space-y-3" data-id="element-1014" data-mp="14r9mml8aspiv">
        <p className="text-teal-400 font-semibold tracking-widest uppercase text-sm" data-id="element-1015" data-mp="uuwlj2afb9e3">
          The Trade-off
        </p>
        <h2 className="text-3xl md:text-5xl font-serif text-white" data-id="element-1016" data-mp="1shddds0e0usg">
          Scale and relevance have been an oxymoron
        </h2>
        <p className="text-lg text-white/60" data-id="element-1017" data-mp="1ngzhf8c29bas">
          Both existing models solve one half of the problem.
        </p>
      </motion.div>

      <div className="flex-1 flex flex-col justify-center max-w-6xl mx-auto w-full space-y-6" data-id="element-1018" data-mp="1eivse21v9we8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1019" data-mp="74sqvkf7qjz8">
          <motion.div initial={{
          opacity: 0,
          x: -20
        }} animate={{
          opacity: stage >= 1 ? 1 : 0,
          x: stage >= 1 ? 0 : -20
        }} className="bg-navy-900/50 border border-navy-700 rounded-2xl p-6 flex flex-col" data-id="element-1020" data-mp="727pewitu620">
            <div className="flex items-center gap-3 mb-4" data-id="element-1021" data-mp="3qs44mztn591x">
              <div className="w-10 h-10 rounded-xl bg-rose-500/10 text-rose-400 flex items-center justify-center" data-id="element-1022" data-mp="3qtoxml0b4jxo">
                <Briefcase className="w-5 h-5" data-id="element-1023" data-mp="3hzalusezjrjb" />
              </div>
              <h3 className="text-xl font-semibold text-white" data-id="element-1024" data-mp="3hxpnerpjvog6">
                The agency model
              </h3>
            </div>
            <p className="text-white/60 text-sm leading-relaxed mb-5" data-id="element-1025" data-mp="381362cwfnfiw">
              Works well for a small number of major campaigns. At scale, it
              becomes:
            </p>
            <ul className="space-y-2.5 flex-1" data-id="element-1026" data-mp="2z0519se673z9">
              {agency.map((item, idx) => <motion.li key={item} initial={{
              opacity: 0,
              x: -8
            }} animate={{
              opacity: stage >= 2 ? 1 : 0,
              x: stage >= 2 ? 0 : -8
            }} transition={{
              delay: stage >= 2 ? idx * 0.08 : 0
            }} className="flex items-start gap-3 text-white/80 text-sm" data-id="element-1027" data-mp="2z2iwc18uog6t" data-mpi={idx}>
                  <span className="w-1.5 h-1.5 rounded-full bg-rose-400 mt-1.5 shrink-0" data-id="element-1028" data-mp="33cbtmv8b0t3j" />
                  {item}
                </motion.li>)}
            </ul>
            <p className="text-white/50 text-sm mt-5 pt-4 border-t border-navy-700 leading-relaxed" data-id="element-1029" data-mp="2yb4n0zhwdzyg">
              Difficult to scale across many brands, markets and campaign
              cycles.
            </p>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          x: 20
        }} animate={{
          opacity: stage >= 3 ? 1 : 0,
          x: stage >= 3 ? 0 : 20
        }} className="bg-navy-900/50 border border-navy-700 rounded-2xl p-6 flex flex-col" data-id="element-1030" data-mp="3m2a39frr2u9a">
            <div className="flex items-center gap-3 mb-4" data-id="element-1031" data-mp="90psc1v1on6f">
              <div className="w-10 h-10 rounded-xl bg-purple-500/10 text-purple-500 flex items-center justify-center" data-id="element-1032" data-mp="dt0tt95foh13">
                <Users className="w-5 h-5" data-id="element-1033" data-mp="is3obznsf4ut" />
              </div>
              <h3 className="text-xl font-semibold text-white" data-id="element-1034" data-mp="3i6ywxl4k7oo0">
                The internalization model
              </h3>
            </div>
            <p className="text-white/60 text-sm leading-relaxed mb-5" data-id="element-1035" data-mp="3m9ltu0g37zs2">
              Increases ownership and production volume. But it can create:
            </p>
            <ul className="space-y-2.5 flex-1" data-id="element-1036" data-mp="1chp5v6psk4">
              {internal.map((item, idx) => <motion.li key={item} initial={{
              opacity: 0,
              x: 8
            }} animate={{
              opacity: stage >= 4 ? 1 : 0,
              x: stage >= 4 ? 0 : 8
            }} transition={{
              delay: stage >= 4 ? idx * 0.08 : 0
            }} className="flex items-start gap-3 text-white/80 text-sm" data-id="element-1037" data-mp="16hepvjcyp3a0" data-mpi={idx}>
                  <span className="w-1.5 h-1.5 rounded-full bg-purple-500 mt-1.5 shrink-0" data-id="element-1038" data-mp="2h9xay7r5fi2h" />
                  {item}
                </motion.li>)}
            </ul>
            <p className="text-white/50 text-sm mt-5 pt-4 border-t border-navy-700 leading-relaxed" data-id="element-1039" data-mp="2d5plu9gs403q">
              Hard to maintain quality across brands and markets.
            </p>
          </motion.div>
        </div>

        <motion.div initial={{
        opacity: 0,
        y: 12
      }} animate={{
        opacity: stage >= 4 ? 1 : 0,
        y: stage >= 4 ? 0 : 12
      }} className="bg-rose-500/10 border border-rose-500/30 rounded-2xl px-7 py-5" data-id="element-1040" data-mp="1yb1ib0uxvbqr">
          <p className="text-white/90 text-lg leading-relaxed" data-id="element-1041" data-mp="1qzwwhp5ouf4u">
            Internalization may solve part of the capacity problem. It does not
            automatically solve the{' '}
            <span className="text-rose-400 font-semibold" data-id="element-1042" data-mp="1h1h6xqmb7dxg">relevance</span>,{' '}
            <span className="text-rose-400 font-semibold" data-id="element-1043" data-mp="1gze76evcu6fj">engagement</span> or{' '}
            <span className="text-rose-400 font-semibold" data-id="element-1044" data-mp="1m13lc3dvj07u">performance</span>{' '}
            problem.
          </p>
        </motion.div>
      </div>
    </div>;
}